import { useState, useEffect, useCallback, useRef } from 'react';
import { generateMockApiResponse, formatLastUpdated } from './solar-health-utils';

/**
 * Custom hook for managing solar health diagnostics data
 */
export const useSolarHealthData = (config, isActive = true) => {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  
  const abortControllerRef = useRef(null);
  const timeoutRef = useRef(null);

  /**
   * Fetch data from API or mock service
   */
  const fetchData = useCallback(async (isRetry = false) => {
    if (!isActive) return;

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    
    if (!isRetry) {
      setIsLoading(true);
    }
    setError(null);

    try {
      let result;
      
      if (config.mockData?.enabled || config.widget?.enableMockData) {
        // Use mock data with simulated delay
        await new Promise(resolve => setTimeout(resolve, 500));
        result = generateMockApiResponse(config);
      } else {
        // Real API call would go here
        const response = await fetch(`${config.api.endpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: config.api.timeout || 30000,
          signal: abortControllerRef.current.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        result = await response.json();
      }

      // Validate response structure
      if (!result || !result.data) {
        throw new Error('Invalid data format received from server');
      }

      setData(result.data);
      setLastUpdated(formatLastUpdated(new Date()));
      setRetryCount(0); // Reset retry count on success
      setError(null);

    } catch (err) {
      if (err.name === 'AbortError') {
        return; // Request was cancelled, don't update state
      }

      console.error('Error fetching solar health data:', err);
      
      const shouldRetry = retryCount < (config.api?.retryAttempts || 3);
      
      if (shouldRetry && !isRetry) {
        // Exponential backoff for retries
        const delay = (config.api?.retryDelay || 2000) * Math.pow(2, retryCount);
        
        timeoutRef.current = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchData(true);
        }, delay);
        
        return;
      }

      setError({
        message: 'Failed to fetch solar health data',
        canRetry: true,
        originalError: err
      });
      setData({});
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isActive, config, retryCount]);

  /**
   * Manual retry function
   */
  const retry = useCallback(() => {
    setRetryCount(0);
    setError(null);
    fetchData();
  }, [fetchData]);

  /**
   * Setup periodic refresh
   */
  useEffect(() => {
    if (!isActive) return;

    fetchData();

    const intervalId = setInterval(() => {
      fetchData();
    }, config.widget?.refreshInterval || 300000); // Default 5 minutes

    return () => {
      clearInterval(intervalId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, isActive]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    data,
    isLoading,
    error,
    lastUpdated,
    retry,
    retryCount
  };
};
